import { LoadingButton } from '@mui/lab';
import { Button, Card, MenuItem, Select } from '@mui/material';
import { useSegment } from 'common/hooks/segment';
import { Input, InputWrapper } from 'components/SignInput';
import { Routes } from 'constants/pages';
import * as React from 'react';
import { useForm, Validate } from 'react-hook-form';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProfilePayload } from 'services/clients/user';
import { useCreateProfile } from './hooks';
import { roles } from './roles';

const MIN_PASSWORD_LENGTH = 8;

interface FormValues extends ProfilePayload {
  customRole: string;
}

interface Props {
  proceed: (email: string) => void;
}

export const ProfileForm: React.FC<Props> = (props) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { track } = useSegment();

  const createMutation = useCreateProfile(props.proceed);

  const email = params.get('email') || '';

  const { register, handleSubmit, setValue, formState, watch, resetField } = useForm<FormValues>({
    defaultValues: {
      email,
      companyRole: '',
    },
    mode: 'all',
  });

  const watchRole = watch('companyRole');

  const onSubmit = handleSubmit((values) => {
    const { customRole, ...rest } = values;
    const payload: ProfilePayload = {
      ...rest,
      companyRole: values.companyRole === 'other' ? values.customRole : values.companyRole,
    };

    const { password, ...everythingElse } = payload;

    createMutation.mutate(payload, {
      onSuccess: () => {
        track('User profile is created', {
          ...everythingElse,
          success: true,
          error: '',
        });
      },
      // eslint-disable-next-line
      onError: (error: any) => {
        // eslint-disable-next-line
        console.error(error);
        track('User profile is created', {
          ...everythingElse,
          success: false,
          error: error.message,
        });
      },
    });
  });

  // Phone has to be registered manually since its input is special
  // Note for validation;
  const phone = register('phoneNumber', {
    required: true,
    validate: {
      length: (number) => {
        return isPossiblePhoneNumber(number) ? true : 'Invalid phone number';
      },
    },
  });

  const validateEmail: Record<string, Validate<string, FormValues>> = {
    length: (password: string) => {
      return password.length >= MIN_PASSWORD_LENGTH
        ? true
        : `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`;
    },
  };

  const validateRole: Record<string, Validate<string, FormValues>> = {
    hasValue: (role: string) => {
      return role ? true : 'Must select a role';
    },
  };

  const disabled = createMutation.isLoading;

  return (
    <Card className="py-6 px-8 text-left shadow-sm border border-gray-50">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First Name*"
            type="text"
            placeholder="Enter your first name"
            inputProps={register('firstName', { required: true })}
            error={formState.errors.firstName}
            disabled={disabled}
          />
          <Input
            label="Last Name*"
            type="text"
            placeholder="Enter your last name"
            inputProps={register('lastName', { required: true })}
            error={formState.errors.lastName}
            disabled={disabled}
          />
        </div>
        <Input
          disabled
          label="Email*"
          type="email"
          inputProps={register('email', { required: true })}
          error={formState.errors.email}
        />
        <Input
          label="Password*"
          type="password"
          placeholder={`${MIN_PASSWORD_LENGTH} characters min`}
          inputProps={register('password', {
            required: true,
            validate: validateEmail,
          })}
          error={formState.errors.password}
          disabled={disabled}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputWrapper label="Role*" error={formState.errors.companyRole} className="w-full">
            <Select
              label="Role"
              fullWidth
              displayEmpty
              inputProps={register('companyRole', {
                validate: validateRole,
                onChange: () => resetField('customRole'),
              })}
              disabled={disabled}
              style={{ height: '45px', fontSize: '14px' }}
            >
              <MenuItem value="" disabled>
                <h4>Role</h4>
              </MenuItem>
              {roles.map((role) => (
                <MenuItem value={role.value} key={role.value}>
                  <h4>{role.label}</h4>
                </MenuItem>
              ))}
            </Select>
          </InputWrapper>
          <InputWrapper
            label="Phone*"
            error={formState.errors.phoneNumber}
            className="w-full signup-phone-input"
          >
            <PhoneInput
              defaultCountry="US"
              onChange={(phoneNumber) => {
                setValue('phoneNumber', phoneNumber ? (phoneNumber as string) : '', {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }}
              onBlur={phone.onBlur}
              ref={phone.ref}
              disabled={disabled}
              className="border-gray-500 h-[45px] input:h-[43px]"
            />
          </InputWrapper>
        </div>
        {watchRole === 'other' && (
          <Input
            label="Company Role*"
            type="text"
            placeholder="Enter your role"
            inputProps={register('customRole', {
              required: true,
            })}
            error={formState.errors.customRole}
          />
        )}
        {createMutation.error ? (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <div className="text-red-900">{(createMutation.error as any).message}</div>
        ) : null}
        <div className="flex space-x-2 pt-8">
          <Button
            variant="outlined"
            color="info"
            size="large"
            className="w-[164px]"
            onClick={() => {
              navigate(Routes.SignUp);
            }}
          >
            <h3>Back</h3>
          </Button>
          <div className="grow" />
          <LoadingButton
            variant="contained"
            type="submit"
            loading={createMutation.isLoading}
            disabled={!formState.isValid}
            size="large"
            className="w-[164px]"
          >
            <h3>Continue</h3>
          </LoadingButton>
        </div>
      </form>
    </Card>
  );
};
ProfileForm.displayName = 'SignUp.ProfileForm';
